/* eslint-disable react/prop-types */
import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { Tabs, Tab, Button } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import LinearProgress from "@material-ui/core/LinearProgress";
import Avatar from "@material-ui/core/Avatar";
// authentication
import { withAxios } from "containers/authenticated.js";
import { getGoalProgress } from "services/userDataProvider.js";
import { MEDIA_ROOT } from "config.js";

const styles = theme => ({
  table: {
    minWidth: 650
  },
  progressBar: {
    width: "100%"
  },
  tableRow: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    },
    "&:last-child td, &:last-child th": {
      border: 0
    },
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.background.default
    },
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.background.paper
    }
  },
  avatarCell: {
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },
  mobileRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "5px",
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      alignItems: "center"
    }
  },
  mobileChart: {
    width: "100%",
    marginTop: "5px",
    [theme.breakpoints.down("xs")]: {
      display: "none"
    }
  },
  groupRow: {
    backgroundColor: theme.palette.grey[200], // Distinct background color for group rows
    fontWeight: "bold"
  },
  headerCell: {
    backgroundColor: theme.palette.primary.light, // Highlight background color
    color: theme.palette.common.white, // White text color
    fontWeight: "bold"
  },
  modeBadge: {
    display: "inline-block",
    marginLeft: "10px",
    padding: "3px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 600,
    backgroundColor:
      (theme.palette.info && theme.palette.info.light) ||
      theme.palette.primary.light,
    color:
      (theme.palette.info && theme.palette.info.contrastText) ||
      theme.palette.common.white,
    verticalAlign: "middle"
  },
  taskStatusPill: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 600
  },
  taskStatusCompleted: {
    backgroundColor:
      (theme.palette.success && theme.palette.success.light) ||
      "rgba(76, 175, 80, 0.2)",
    color:
      (theme.palette.success && theme.palette.success.contrastText) || "#1b5e20"
  },
  taskStatusPending: {
    backgroundColor:
      (theme.palette.warning && theme.palette.warning.light) ||
      "rgba(255, 152, 0, 0.2)",
    color:
      (theme.palette.warning && theme.palette.warning.contrastText) || "#e65100"
  }
});

class GoalProgressLeaderboard extends Component {
  constructor(props) {
    super(props);
    const goalIdFromPath =
      props.match && props.match.params && props.match.params.goalId;
    this.state = {
      goalId: props.goalId || goalIdFromPath, // Use goalId from props or fallback to path
      goalDetails: null, // Goal details object
      progressDetailsThisWeek: [], // Array of { member_id, group_id, group_name, member_username, member_avatar, progress_type, progress_value, created_at }
      progressDetailsLastWeek: [], // Array of { member_id, group_id, group_name, member_username, member_avatar, progress_type, progress_value, created_at }
      activeTab: 0 // Track active tab (0 = This Week, 1 = Last Week)
    };
  }

  componentDidMount() {
    let { goalId, progressData } = this.props;
    if (goalId == null) {
      goalId = this.state.goalId;
    }

    if (!goalId) {
      return;
    }

    if (!progressData) {
      getGoalProgress(this.props.axios, goalId).then(data => {
        const { goal, progress } = data;
        const progressThisWeek = (progress && progress.this_week) || [];
        const progressLastWeek = (progress && progress.last_week) || [];
        this.setState({
          goalDetails: goal,
          progressDetailsThisWeek: progressThisWeek.sort(
            (a, b) => b.progress_value - a.progress_value
          ), // Sort by progress_value in descending order
          progressDetailsLastWeek: progressLastWeek.sort(
            (a, b) => b.progress_value - a.progress_value
          ) // Sort by progress_value in descending order
        });
      });
    } else {
      const { goal, progress } = progressData;
      const progressThisWeek = (progress && progress.this_week) || [];
      const progressLastWeek = (progress && progress.last_week) || [];
      this.setState({
        goalDetails: goal,
        progressDetailsThisWeek: progressThisWeek.sort(
          (a, b) => b.progress_value - a.progress_value
        ), // Sort by progress_value in descending order
        progressDetailsLastWeek: progressLastWeek.sort(
          (a, b) => b.progress_value - a.progress_value
        ) // Sort by progress_value in descending order
      });
    }
  }

  handleTabChange = (event, newValue) => {
    this.setState({ activeTab: newValue });
  };

  groupAndSortProgress = progressDetails => {
    const groupedProgress = {};

    // Group progress by group_id
    progressDetails.forEach(progress => {
      const groupId =
        progress.group_id || progress.progress_type || "individual";
      if (!groupedProgress[groupId]) {
        groupedProgress[groupId] = {
          groupName:
            progress.group_name ||
            progress.member_username ||
            progress.member_name ||
            "Task Progress",
          totalProgress: 0,
          members: []
        };
      }
      groupedProgress[groupId].totalProgress += progress.progress_value;
      groupedProgress[groupId].members.push(progress);
    });

    // Sort members within each group by individual progress
    Object.values(groupedProgress).forEach(group => {
      group.avgProgressPerMember =
        group.totalProgress / (group.members.length || 1);
      group.members.sort((a, b) => b.progress_value - a.progress_value);
    });

    // Sort groups by total progress
    return Object.values(groupedProgress).sort(
      (a, b) => b.avgProgressPerMember - a.avgProgressPerMember
    );
  };

  getAllGoalTasks = goalDetails => {
    const milestones = (goalDetails && goalDetails.milestones) || [];
    const tasks = [];

    milestones.forEach(milestone => {
      const milestoneTasks = milestone.tasks || [];
      milestoneTasks.forEach(task => {
        tasks.push({
          ...task,
          milestone_name: milestone.name || "Milestone"
        });
      });
    });

    return tasks.sort((a, b) => {
      if (a.completed !== b.completed) {
        return a.completed ? 1 : -1;
      }
      if (!a.target_date && !b.target_date) {
        return 0;
      }
      if (!a.target_date) {
        return 1;
      }
      if (!b.target_date) {
        return -1;
      }
      return new Date(a.target_date) - new Date(b.target_date);
    });
  };

  render() {
    const { classes } = this.props;
    const {
      goalDetails,
      progressDetailsThisWeek,
      progressDetailsLastWeek,
      activeTab
    } = this.state;
    const goalIdFromPath =
      this.props.match &&
      this.props.match.params &&
      this.props.match.params.goalId;

    if (!goalDetails) {
      return <div>Loading...</div>;
    }

    const allProgressRows = progressDetailsThisWeek.concat(
      progressDetailsLastWeek
    );
    const hasMemberProgress = allProgressRows.some(
      row => row && row.member_id != null
    );
    const isTaskBasedGoal =
      !hasMemberProgress && Number(goalDetails.total_task_count || 0) > 0;

    const selectedTab = !isTaskBasedGoal && activeTab === 2 ? 0 : activeTab;
    const progressDetails =
      selectedTab === 0 ? progressDetailsThisWeek : progressDetailsLastWeek;

    const allTasks = isTaskBasedGoal ? this.getAllGoalTasks(goalDetails) : [];

    const completionMetric = isTaskBasedGoal
      ? "tasks"
      : goalDetails.target_metric;
    const completionTargetValue = isTaskBasedGoal
      ? Number(goalDetails.total_task_count || 0)
      : Number(goalDetails.target_value || 0);
    const safeTargetForPercent =
      completionTargetValue > 0 ? completionTargetValue : 1;

    const totalProgressValue = isTaskBasedGoal
      ? Number(goalDetails.completed_task_count || 0)
      : progressDetailsThisWeek.reduce(
          (sum, progress) => sum + progress.progress_value,
          0
        );

    const percentageCompletion = isTaskBasedGoal
      ? Number(goalDetails.overall_progress_percentage || 0)
      : (totalProgressValue / safeTargetForPercent) * 100;

    const groupedProgress = this.groupAndSortProgress(progressDetails);
    const isSingleGroup = groupedProgress.length === 1;

    return (
      <div>
        {goalIdFromPath && (
          <Button
            color="info"
            onClick={() => (window.location.href = "/my-goals")}
          >
            <ArrowBackIcon style={{ marginRight: "8px" }} />
            Back to Goals
          </Button>
        )}
        <h2 style={{ fontSize: "1.5rem" }}>
          {goalDetails.name} - Progress Leaderboard
          {isTaskBasedGoal ? (
            <span className={classes.modeBadge}>Task-based goal</span>
          ) : null}
        </h2>
        <p>
          <strong>Target:</strong> {completionTargetValue} {completionMetric}{" "}
          <br />
          <strong>Total Completion:</strong> {totalProgressValue.toFixed(1)}{" "}
          {completionMetric} -- {percentageCompletion.toFixed(1)}%
        </p>
        <LinearProgress
          variant="determinate"
          value={percentageCompletion}
          className={classes.progressBar}
        />
        <Tabs
          value={selectedTab}
          onChange={this.handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab label="This Week" />
          <Tab label="Last Week" />
          {isTaskBasedGoal ? <Tab label="All Tasks" /> : null}
        </Tabs>
        <div component={Paper} style={{ overflowX: "auto" }}>
          {selectedTab === 2 && isTaskBasedGoal ? (
            <Table className={classes.table} aria-label="all tasks table">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>Task</TableCell>
                  <TableCell className={classes.headerCell}>
                    Milestone
                  </TableCell>
                  <TableCell className={classes.headerCell}>Status</TableCell>
                  <TableCell className={classes.headerCell}>
                    Target Date
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allTasks.length ? (
                  allTasks.map(task => (
                    <TableRow key={task.id} className={classes.tableRow}>
                      <TableCell>{task.name}</TableCell>
                      <TableCell>{task.milestone_name}</TableCell>
                      <TableCell>
                        <span
                          className={`${classes.taskStatusPill} ${
                            task.completed
                              ? classes.taskStatusCompleted
                              : classes.taskStatusPending
                          }`}
                        >
                          {task.completed ? "Completed" : "Pending"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {task.target_date
                          ? new Date(task.target_date).toLocaleDateString()
                          : "No date"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4}>
                      No tasks found for this goal.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          ) : (
            <Table className={classes.table} aria-label="leaderboard table">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>
                    Member/Group Contribution
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedProgress.length ? (
                  groupedProgress.map((group, groupIndex) => (
                    <React.Fragment key={groupIndex}>
                      {!isSingleGroup && (
                        <TableRow className={classes.groupRow}>
                          <TableCell>
                            <strong>{group.groupName}</strong>
                            <br /> - Total: {group.totalProgress.toFixed(
                              1
                            )}{" "}
                            {completionMetric}
                            <br />- Average per member:{" "}
                            {group.avgProgressPerMember.toFixed(1)}{" "}
                            {completionMetric}
                          </TableCell>
                        </TableRow>
                      )}
                      {group.members.map((member, memberIndex) => (
                        <TableRow
                          key={memberIndex}
                          className={classes.tableRow}
                        >
                          <TableCell component="th" scope="row">
                            <div className={classes.mobileRow}>
                              <div className={classes.avatarCell}>
                                {member.member_avatar ? (
                                  <Avatar
                                    src={`${MEDIA_ROOT}${member.member_avatar}`}
                                    alt={
                                      member.member_username ||
                                      member.member_name
                                    }
                                  />
                                ) : (
                                  <Avatar>
                                    {(
                                      member.member_name ||
                                      member.member_username ||
                                      "Task"
                                    )
                                      .split(" ")
                                      .map(name => name[0])
                                      .join("")
                                      .toUpperCase()}
                                  </Avatar>
                                )}
                                {member.member_name ||
                                  member.member_username ||
                                  "Task Summary"}
                              </div>
                              <span>
                                {member.progress_value.toFixed(1)}{" "}
                                {member.target_metric || completionMetric}
                              </span>
                            </div>
                            <LinearProgress
                              variant="determinate"
                              value={
                                (member.progress_value /
                                  (member.target_value ||
                                    safeTargetForPercent)) *
                                100
                              }
                              className={classes.mobileChart}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell>No progress records found.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    );
  }
}

export default withAxios(withStyles(styles)(GoalProgressLeaderboard));
